import ArrowPathUp from '@/assets/svgs/arrow-path-up.svg';
import ChevronRight from '@/assets/svgs/chevron-right-small.svg';
import CircleCheck from '@/assets/svgs/circle-check.svg';
import { inter600 } from '@/config/fonts';
import { Account } from '@/lib/redux/app.slice';
import classNames from 'classnames';
import React from 'react';
import AccountAvatar from './AccountAvatar';

interface AccountParentChainProps {
  parentChain: Account[];
  currentAccountId: string | null;
  onAccountSelect: (account: Account) => void;
  canSwitchTo: (accountId: string) => boolean;
}

/**
 * Displays the parent account hierarchy for the currently selected account.
 * Shows accounts from root to current in a nested/indented structure.
 * Parent accounts are clickable if the user has permission to switch to them.
 */
const AccountParentChain: React.FC<AccountParentChainProps> = ({
  parentChain,
  currentAccountId,
  onAccountSelect,
  canSwitchTo,
}) => {
  // Don't show anything if chain is empty or only has one account (no parents)
  if (parentChain.length <= 1) {
    return null;
  }

  return (
    <div className="border-base-100 mb-3 border-b pb-3 dark:border-gray-800">
      <div className="mb-2 px-2">
        <span className="text-xs font-medium tracking-wide text-gray-500 uppercase">
          Current Selection
        </span>
      </div>
      <div className="space-y-0.5">
        {parentChain.map((account, index) => {
          const isCurrentAccount = account.account_id === currentAccountId;
          const canSwitch = canSwitchTo(account.account_id);
          const isRootAccount = index === 0; // First account in chain is root

          return (
            <div key={account.account_id}>
              <button
                className={classNames(
                  'flex w-full items-center gap-2 rounded-md p-2 text-left transition-colors',
                  {
                    'hover:bg-base-100': canSwitch,
                    'cursor-pointer': canSwitch,
                    'cursor-default opacity-70': !canSwitch,
                  }
                )}
                onClick={() => canSwitch && onAccountSelect(account)}
                disabled={!canSwitch}>
                {/* Arrow icon for child accounts */}
                {!isRootAccount && (
                  <ArrowPathUp className="h-4 w-4 flex-shrink-0 text-gray-400" />
                )}

                <AccountAvatar
                  accountId={account.account_id}
                  accountName={account.account_name}
                  size="sm"
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={classNames(
                      inter600.className,
                      'text-base-content truncate text-sm'
                    )}
                    title={account.account_name}>
                    {account.account_name}
                  </span>
                  <span className="text-base-content/70 truncate text-xs">
                    {account.account_id}
                  </span>
                </div>
                {isCurrentAccount && (
                  <CircleCheck className="text-primary h-4 w-4 flex-shrink-0" />
                )}
                {!isCurrentAccount && canSwitch && (
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AccountParentChain;
